/* eslint-disable camelcase */
import qs from 'qs'
import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  ArticleResponse,
  BlocksAutoRatesConfigProductComponentProduct,
  BlogCategory,
  BlogPostResponse,
  BlogPostsResponseCustom,
  CategoryListResponse,
  CategoryListResponseDataItemCustom,
  CategoryResponse,
  GlobalResponse,
  MediaArticleListResponse,
  MediaArticleResponse,
  NavigationResponse,
  PageListResponse,
  PageResponse,
  SearchArticlesResponse,
  SectionsEshopsComponent,
  StaticTextsConfigResponse,
} from '@twisto/api-cms'
import type {
  BenefitsData,
  EshopCategoryApiField,
  EshopsData,
} from '@twisto/api-cms/custom-types'
import { envConfig } from '@twisto/base/env-config'
import { getInstallmentsConfigUrl } from '@twisto/base/fetch-installments-config'
import { remoteBackend } from '@twisto/environment'

import type { SectionType } from '../components'

import type { AutoRatesData } from './auto-rates/paths'
import { getConfigURLPath, getExampleURLSegment } from './auto-rates/paths'
import { fetchCmsJson, getCachedCmsJson } from './cms-cache'
import {
  CmsUnavailableError,
  fetchJson,
  isCmsClientError,
  isCmsNotFoundError,
} from './fetch-json'
import { readInstallmentsCalculatorConfig } from './installments-config.helpers'
import {
  calculatorConfigCZ,
  calculatorConfigPL,
} from './mocks/calculator-config'
import installmentsConfigCZ from './mocks/installments-config-cz'
import installmentsConfigPL from './mocks/installments-config-pl'

export type EshopCategory = NonNullable<SectionsEshopsComponent['category']>

export const CATEGORY_TO_INCLUDE_PARAM: Record<
  EshopCategory,
  EshopCategoryApiField
> = {
  popular: 'popular_eshops',
  newest: 'newest_eshops',
  'pay-in-3': 'pay_in_three_eshops',
  'pay-in-30': 'merchant_benefits',
  all: 'all',
}

export const ADDITIONAL_DATA_ENPOINTS = {
  benefits: 'benefits',
  eshops: {
    popular: 'eshops/popular',
    list: 'eshops/list',
  },
}

type StandardPageResponse = PageResponse & {
  data?: {
    title: string
    subtitle: string
    isRoot?: boolean
  }
  attributes: { contentSections?: SectionType[] }
}

type RootPageResponse = PageResponse & {
  title: string
  subtitle: string
  isRoot?: boolean
  attributes: { contentSections?: SectionType[] }
}

export const getStrapiURL = (path: string) =>
  `${process.env.STRAPI_API_URL ?? 'http://localhost:1337/api'}${path}`

export const getQueryString = (params: Record<string, unknown>): string => {
  const query = qs.stringify(params, {
    encodeValuesOnly: true,
  })

  return query.length > 0 ? `?${query}` : ''
}

const fetchAPI = async <T>(
  requestUrl: string,
  urlParamsObject = {},
  options = {}
): Promise<T> => {
  const mergedOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  }

  const queryString = getQueryString(urlParamsObject)
  try {
    return await fetchJson<T>(`${requestUrl}${queryString}`, mergedOptions)
  } catch (error) {
    const fallback = {}

    return fallback as T
  }
}

export const fetchStrapiResponse = async <T>(
  path: string,
  urlParamsObject = {},
  useCache = true
): Promise<T> => {
  const requestUrl = `${getStrapiURL(path)}${getQueryString(urlParamsObject)}`

  return fetchCmsJson<T>(requestUrl, useCache)
}

export const fetchStrapiAPI = async <T>(
  path: string,
  urlParamsObject = {},
  useCache = true
): Promise<T> => {
  try {
    return await fetchStrapiResponse<T>(path, urlParamsObject, useCache)
  } catch (error) {
    if (!isCmsNotFoundError(error)) {
      if (isCmsClientError(error)) {
        throw new CmsUnavailableError()
      }
      throw error
    }
    // SSR resource lookups use absence to choose a route or return notFound.
    const missing = { data: null }

    return missing as T
  }
}

export const getPages = async (): Promise<PageListResponse> =>
  await fetchStrapiAPI('/pages', { fields: 'path' })

export const getPage = async (url: string): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/page/${encodeURIComponent(url)}`, {})

export const getCachedPage = (url: string) =>
  getCachedCmsJson<StandardPageResponse>(
    getStrapiURL(`/page/${encodeURIComponent(url)}`)
  )

export const getConditionsPage = async (
  url: string
): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/${encodeURIComponent(url)}`, {})

export const getConditionsRootPage = async (): Promise<RootPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/root`, {})

export const getConditionsSection = async (): Promise<PageResponse> =>
  await fetchStrapiAPI(`/conditions-sections`, {
    populate: {
      conditions_pages: true,
      conditions_folders: {
        populate: ['conditions_pages'],
      },
    },
  })

export const getNavigation = async (
  id: string | number,
  useCache = true
): Promise<NavigationResponse> =>
  await fetchStrapiAPI(`/navigations/${id}`, {}, useCache)

export const getMediaArticle = async (
  path: string
): Promise<MediaArticleResponse> =>
  await fetchStrapiAPI(`/media-article/${encodeURIComponent(path)}`, {})

export const getMediaArticles = async (): Promise<MediaArticleListResponse> =>
  await fetchStrapiAPI('/media-articles', {})

export const getGlobalMetadata = async (): Promise<GlobalResponse> =>
  await fetchStrapiAPI('/global', {})

export const getCachedGlobalMetadata = () => {
  // Global SEO defaults are optional; warm the cache without delaying page content.
  getGlobalMetadata().catch(() => undefined)

  return getCachedCmsJson<GlobalResponse>(getStrapiURL('/global'))
}

export const getCategories = async (): Promise<CategoryListResponse> =>
  await fetchStrapiAPI('/categories', { fields: 'path' })

export const getCategory = async (
  url: string,
  useCache = true
): Promise<
  CategoryResponse & {
    data: CategoryListResponseDataItemCustom
  }
> =>
  await fetchStrapiAPI(`/categories/${encodeURIComponent(url)}`, {}, useCache)

export const getArticle = async (url: string): Promise<ArticleResponse> =>
  await fetchStrapiAPI(`/articles/${encodeURIComponent(url)}`, {})

export const getArticles = async (): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', { fields: 'path' })

export const getStaticTexts = async (
  useCache = true
): Promise<StaticTextsConfigResponse> =>
  await fetchStrapiAPI('/static-texts-config', {}, useCache)

export const getPopularArticles = async (
  rootCategoryId?: string | number,
  useCache = true
): Promise<ArticleListResponse> =>
  await fetchStrapiAPI(
    '/articles',
    {
      'filters[isPopular]': true,
      'pagination[pageSize]': 15,
      ...(!isNil(rootCategoryId) && {
        'filters[rootCategoryId]': rootCategoryId,
      }),
    },
    useCache
  )

export const searchArticles = async (
  searchTerm: string,
  pageSize: number,
  rootCategoryId?: number
): Promise<SearchArticlesResponse> =>
  await fetchAPI('/api/article-search', {
    query: encodeURIComponent(searchTerm),
    'pagination[articles][pageSize]': pageSize,
    ...(!isNil(rootCategoryId) && {
      'filters[articles][rootCategoryId]': rootCategoryId,
    }),
    'populate[articles][category][populate][parent][fields][0]': '*',
  })

export const getBlogPost = async (url: string): Promise<BlogPostResponse> =>
  await fetchStrapiAPI(`/blog_post/${encodeURIComponent(url)}`, {})

export const getBlogPosts = async (
  page: number,
  pageSize: number,
  categoryId?: string | number,
  newest?: boolean
): Promise<BlogPostsResponseCustom | null> => {
  const data = await fetchJson<Partial<BlogPostsResponseCustom>>(
    `/api/blog-posts${getQueryString({ page, pageSize, categoryId, newest })}`
  ).catch(() => null)
  if (!data?.meta?.pagination || !Array.isArray(data.posts)) {
    return null
  }

  return { posts: data.posts, meta: data.meta }
}

export const getBlogCategories = async (): Promise<BlogCategory[] | null> => {
  const categories = await fetchJson<BlogCategory[] | null>(
    '/api/blog-categories'
  ).catch(() => null)

  return Array.isArray(categories) ? categories : null
}

export const getAdditionalData = async <T>(
  endpoint: string
): Promise<T | null> => {
  // Deployed Homepage data stays pinned to its CMS configuration, not Host.
  const baseUrl =
    remoteBackend?.apiProxyOrigin ??
    (process.env.CMS_API_URL ?? '')
      .replace(/https?:\/\/cms\./, 'https://api.')
      .replace(/\/api$/, '')
  const url = `${baseUrl}/${endpoint}`

  return await fetchAPI(url, {})
}

export const getEshops = async (
  endpoint: keyof typeof ADDITIONAL_DATA_ENPOINTS.eshops,
  category?: string
): Promise<EshopsData | null> => {
  const shouldIncludeCategory = category && category !== 'all'
  const requestUrl = `${ADDITIONAL_DATA_ENPOINTS.eshops[endpoint]}${shouldIncludeCategory ? `?include=${category}` : ''}`

  if (envConfig.MOCK_API) {
    return {
      eshops: [],
      eshop_categories: [],
      popular_eshops: [],
      newest_eshops: [],
      pay_in_three_eshops: [],
    }
  }

  if (!requestUrl) {
    return null
  }

  return await getAdditionalData<EshopsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const getBenefits = async (): Promise<BenefitsData | null> => {
  const requestUrl = ADDITIONAL_DATA_ENPOINTS.benefits

  return await getAdditionalData<BenefitsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const validateInvoiceId = async (
  invoiceId: string,
  email: string
): Promise<{ valid: boolean } | null> => {
  const apiOrigin = window.location.origin.replace('//www.', '//api.')

  const response = await fetch(`${apiOrigin}/invoice/validate/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invoice_id: invoiceId,
      email,
    }),
  })

  if (!response.ok) {
    return null
  }

  const data = await response.json()

  return data
}

/** Calculators Config Api
 * - deferral
 * - instalments
 */

export const fetchDeferredCalcConfig = async (
  baseUrl: string,
  region: 'CZ' | 'PL'
) => {
  const previousConfig =
    region === 'PL' ? calculatorConfigPL : calculatorConfigCZ

  if (envConfig.MOCK_API) {
    return previousConfig
  }

  try {
    return await fetchJson<typeof previousConfig>(
      `${baseUrl}/deferred-payment/config`
    )
  } catch {
    return previousConfig
  }
}

export const fetchInstallmentsCalcConfig = async (
  baseUrl: string,
  region?: 'CZ' | 'PL'
) => {
  const resolvedRegion = region ?? (baseUrl.includes('pl') ? 'PL' : 'CZ')
  const previousConfig =
    resolvedRegion === 'PL' ? installmentsConfigPL : installmentsConfigCZ

  if (envConfig.MOCK_API) {
    return previousConfig
  }

  // Config endpoints are not expected to fail. The fallback is added for calculators UI to work during migration
  try {
    return readInstallmentsCalculatorConfig(
      await fetchJson(getInstallmentsConfigUrl(baseUrl, false), {
        headers: { 'Content-Type': 'application/json' },
      })
    )
  } catch {
    return previousConfig
  }
}

export const fetchAutoRatesConfig = async (
  baseUrl: string,
  product: BlocksAutoRatesConfigProductComponentProduct
): Promise<AutoRatesData | undefined> => {
  const path = getConfigURLPath(product)
  if (!path) {
    return undefined
  }

  const url = `${baseUrl}/${path}`

  try {
    return { [product]: await fetchJson(url) }
  } catch (err) {
    return {}
  }
}

export const fetchAutoRatesExample = async (
  baseUrl: string,
  product: BlocksAutoRatesConfigProductComponentProduct,
  amount: string
): Promise<AutoRatesData | undefined> => {
  const segment = getExampleURLSegment(product)
  if (!segment) {
    return undefined
  }

  const url = `${baseUrl}/${segment}/config/example/?amount=${amount}`

  try {
    return { [product]: await fetchJson(url) }
  } catch {
    return {}
  }
}
